import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { When } from 'react-if';
import { Tooltip } from '@mui/material';

import { classNames }  from '../lib/classNames';
import { IHeroIcon } from '../../main/types/types';

import { Badge } from './Badge';

export interface ITab {
  name: string;
  href: string;
  icon?: IHeroIcon;
  new?: boolean;
  /** Shown greyed out and not clickable */
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
  current?: boolean;
  badge?: {
    text: string;
    color: string;
    tooltip?: string;
  };
  secondaryBadge?: {
    text: string;
    className?: string;
    tooltip?: string;
  };
}

interface ITabsProps {
  tabs: ITab[];
  navigational?: boolean;
  className?: string;
  tabStyle?: `default` | `v2`;
  small?: boolean;
}

// Resolve which tab is current from the route (navigational) or the `current` flag.
export function useCurrentTabName(tabs: ITab[], navigational = true) {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (navigational) {
      const parts = pathname.split(`/`).filter(Boolean);
      const lastPart = parts[parts.length - 1] || ``;
      const exactLastSegmentMatch = tabs.find(tab => tab.href === lastPart);

      if (exactLastSegmentMatch) {
        return exactLastSegmentMatch.name;
      }

      const segmentMatch = tabs.find(tab => tab.href !== `` && parts.includes(tab.href));

      if (segmentMatch) {
        return segmentMatch.name;
      }

      // Keep existing index-tab fallback behavior for empty href routes.
      return tabs.find(tab => tab.href === ``)?.name;
    }
    else {
      return tabs.find(tab => tab.current)?.name;
    }
  }, [pathname, tabs, navigational]);
}

export  const Tabs = forwardRef<HTMLDivElement, ITabsProps>(({
  tabs,
  navigational = true,
  className,
  tabStyle = `default`,
  small,
}, ref) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const [useDropdown, setUseDropdown] = useState<boolean>(false);

  const currentTab = useCurrentTabName(tabs, navigational);

  function handleMobileChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const selectedTab = tabs.find(tab => tab.name === e.target.value);

    if (selectedTab && selectedTab.onClick) {
      selectedTab.onClick();
    }

    navigate(selectedTab?.href);
  }

  useEffect(() => {
    const container = containerRef.current;
    const nav = navRef.current;
    if (!container || !nav) return;

    const checkOverflow = () => {
      // If the combined width of tabs (scrollWidth) exceeds available container width, use dropdown
      const needsDropdown = nav.scrollWidth > container.clientWidth;
      setUseDropdown(needsDropdown);
    };

    // Initial check
    checkOverflow();

    const containerObserver = new ResizeObserver(checkOverflow);
    const navObserver = new ResizeObserver(checkOverflow);
    containerObserver.observe(container);
    navObserver.observe(nav);
    window.addEventListener(`resize`, checkOverflow);

    return () => {
      containerObserver.disconnect();
      navObserver.disconnect();
      window.removeEventListener(`resize`, checkOverflow);
    };
  }, [tabs, tabStyle, small]);

  return (
    <div
      ref={ ref }
      className={ classNames(
        tabStyle === `v2` ? `py-4` : `pb-6`,
        className,
      ) }>
      <div className={ tabStyle === `v2` ? `` : `border-b border-gray-200` }>
        <div ref={ containerRef }
          className={ `w-full` }>
          <div className={ classNames(useDropdown ? `block` : `hidden`) }>
            <label htmlFor={ `tabs` }
              className={ `sr-only` }>
              { `Select a tab` }
            </label>
            { /* Use an "onChange" listener to redirect the user to the selected tab URL. */ }
            <select
              id={ `tabs` }
              name={ `tabs` }
              className={ `block w-full rounded-md border-gray-300` }
              defaultValue={ currentTab }
              onChange={ handleMobileChange }
            >
              { tabs.map(tab => (
                <option
                  key={ tab.name }
                  disabled={ tab.disabled }
                >
                  { tab.name }
                </option>
              )) }
            </select>
          </div>
          <nav
            ref={ navRef }
            // Hide visually if using dropdown, but keep in DOM for measurement
            className={ classNames(
              tabStyle === `v2` ? `-mb-px flex justify-center` : `-mb-px flex space-x-8`,
              useDropdown ? `invisible absolute -z-10 pointer-events-none` : ``,
            ) }
            aria-label={ `Tabs` }>
            { tabs.map((tab, tabIdx) => {
              const current = tab.name === currentTab || tab.current;

              const el = (
                <Link
                  key={ tab.name }
                  to={ tab.href }
                  onClick={ e => {
                    if (tab.disabled) {
                      e.preventDefault();

                      return;
                    }

                    if (tab.onClick) {
                      // When onClick is defined, we override the default behavior
                      e.preventDefault();
                      tab.onClick();
                    }
                  } }
                  className={
                    tabStyle === `v2` ?
                      classNames(
                        `relative group inline-flex items-center py-4 px-12 lg:px-12 w-full justify-center text-sm font-medium bg-white`,
                        `border`,
                        tabIdx === 0 ? `border-r-0 rounded-l-md` : `border-r`,
                        tabIdx !== tabs.length - 1 ? `border-r-0 rounded-r-0` : `border-r rounded-r-md`,
                        current
                          ? `text-paidniceGreen-600`
                          : tab.disabled
                            ? `text-gray-400 cursor-not-allowed`
                            : ` text-gray-500 hover:text-gray-700 hover:bg-gray-50`,

                      ) :
                      classNames(
                        current
                          ? `border-paidniceGreen-500 text-paidniceGreen-600`
                          : tab.disabled
                            ? `border-transparent text-gray-400 cursor-not-allowed`
                            : ` text-gray-500 hover:border-gray-300 hover:text-gray-700`,
                        `group inline-flex items-center border-b-2 px-1 text-sm font-medium`,
                        small ? `py-1 font-normal` : `py-4`,
                      ) }
                  aria-current={ current ? `page` : undefined }
                  aria-disabled={ tab.disabled || undefined }
                  tabIndex={ tab.disabled ? -1 : undefined }
                >
                  <When condition={ !!tab.icon }>
                    { () => (
                      <tab.icon
                        className={ classNames(
                          current ? `text-paidniceGreen-500` : `text-gray-400 group-hover:text-gray-500`,
                          `-ml-0.5 mr-2 h-5 w-5`,
                        ) }
                        aria-hidden={ `true` }
                      />
                    ) }
                  </When>
                  <span className={ `mr-2` }>{ tab.name }</span>
                  <When condition={ tabStyle === `v2` }>
                    { /* The bottom border */ }
                    <span
                      aria-hidden={ `true` }
                      className={ classNames(
                        current ? `bg-paidniceGreen-400` : `bg-transparent`,
                        `absolute inset-x-0 bottom-0 h-0.5`,
                      ) }
                    />
                  </When>
                  <When condition={ !!tab.badge }>
                    {
                      () => (
                        tabStyle === `v2` ?
                          (
                            <Tooltip
                              disableHoverListener={ !tab.badge.tooltip }
                              title={ tab.badge.tooltip }
                            >
                              <span
                                className={ classNames(
                                  current ? `bg-paidniceGreen-100 text-paidniceGreen-600` : `bg-gray-100 text-gray-900`,
                                  `ml-1 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block`,
                                ) }
                              >
                                { tab.badge.text }
                              </span>
                            </Tooltip>

                          ) : (
                            <Badge
                              color={ tab.badge.color }
                              size={ `xs` }
                            >
                              { tab.badge.text }
                            </Badge>
                          )
                      )
                    }
                  </When>
                  <When condition={ !!tab.secondaryBadge }>
                    {
                      () => (
                        tabStyle === `v2` ?
                          (
                            <Tooltip
                              disableHoverListener={ !tab.secondaryBadge.tooltip }
                              title={ tab.secondaryBadge.tooltip }
                            >
                              <span
                                className={ classNames(
                                  tab.secondaryBadge.className,
                                  `ml-1 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block`,
                                ) }
                              >
                                { tab.secondaryBadge.text }
                              </span>
                            </Tooltip>

                          ) : (
                            <Badge
                              size={ `xs` }
                            >
                              { tab.secondaryBadge.text }
                            </Badge>
                          )
                      )
                    }
                  </When>
                  <When condition={ tab.new }>
                    <Badge
                      color={ `purple` }
                    >
                      { `NEW` }
                    </Badge>
                  </When>
                </Link>
              );

              if (tab.tooltip) {
                return (
                  <Tooltip
                    key={ tab.name }
                    title={ tab.tooltip }
                  >
                    { el }
                  </Tooltip>
                );
              }

              return el;
            }) }
          </nav>
        </div>
      </div>
    </div>
  );
});

Tabs.displayName = `Tabs`;
